import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import RetailerFitGuide_TechSpecs from './RetailerFitGuide_TechSpecs.jsx';
import RetailerFitGuide_CompatibleProducts from './RetailerFitGuide_CompatibleProducts.jsx';
import { RetailerFitGuide_Select } from '../Shared/RetailerFitGuide_Select.jsx';
import RetailerFitGuide_CarSelector from '../Shared/RetailerFitGuide_CarSelector.jsx';
import {
  getSolutionReferenceComponents,
  getReferenceDisplayNameById,
  isValidSolutionType,
} from '../Script/RetailerFitGuide_Utils.js';
import ImageSlider from '/Foundation/React/code/Components/ImageSlider';
import { RetailerFitGuide_ClearCar } from '../Shared/RetailerFitGuide_ClearCar.jsx';
import RetailerFitGuide_FitTips from '../Shared/RetailerFitGuide_FitTips.jsx';
import RetailerFitGuide_ImageGrid from './RetailerFitGuide_ImageGrid.jsx';
import { useAppState } from '../RetailerFitGuide_StateContext.jsx';
import { track_addToList, track_linkClick, track_shareLink } from '../Script/RetailerFitGuide_Tracking.js';
import { TRACK_EVENT_TYPE, SOLUTION_REFERENCE_TYPE } from '../Script/RetailerFitGuide_Constants.js';
import RetailerFitGuide_Downloads from '../Shared/RetailerFitGuide_Downloads.jsx';

const RetailerFitGuide_PDP = () => {
  const { productList, setProductList, translations, setNotificationMessage, selectedSolution, setSelectedSolution } =
    useAppState();

  const [carResult, setCarResult] = useState();
  const [solutionsDropDown, setSolutionsDropDown] = useState();
  const [selectedSolutionsDropDown, setSelectedSolutionsDropDown] = useState();
  const [solutionDataProduct, setSolutionDataProduct] = useState();
  const [activeColor, setActiveColor] = useState('');
  const [activeSize, setActiveSize] = useState('');
  const carSelectorRef = useRef(null);
  const carSelectorDesktopRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const year = queryParams.get('year');
  const make = queryParams.get('make');
  const model = queryParams.get('model');
  const variationId = queryParams.get('variationId');
  const bedTypeLovId = queryParams.get('bedTypeLovId');
  const solutionId = queryParams.get('solutionId');

  const queryParameters = useMemo(() => {
    if (year || make || model || variationId || solutionId || bedTypeLovId) {
      return { year, make, model, variation: variationId, solutionId, bedTypeLovId };
    }
    return null;
  }, [year, make, model, variationId, solutionId, bedTypeLovId]);

  const setUrlParameters = (year, make, model, variationId, solutionId, bedTypeLovId) => {
    const queryParams = new URLSearchParams({
      solutionId,
      make,
      year,
      model,
      variationId,
      bedTypeLovId,
    });
    navigate(
      {
        pathname: location.pathname,
        search: queryParams.toString(),
      },
      { replace: true }
    );
  };
  useEffect(() => {
    if (!selectedSolution) {
      if (solutionDataProduct) setSolutionDataProduct(null);
      return;
    }

    loadSolutionDataProduct();

    if (carResult) {
      setUrlParameters(
        carResult?.year,
        carResult?.make,
        carResult?.model,
        carResult.carVariationLovId,
        selectedSolution?.solutionId,
        carResult?.bedTypeLovId
      );
    }
  }, [selectedSolution]);

  const onChangeSelectedSolution = (solutionDropDown) => {
    setSelectedSolutionsDropDown(solutionDropDown);
    setSelectedSolution(solutionDropDown.solutions[0]);
  };

  useEffect(() => {
    if (!carResult) {
      setSelectedSolution(null);
      setSolutionsDropDown(null);
      setSelectedSolutionsDropDown(null);
      return;
    }

    //changed car -> set url accordingly
    setUrlParameters(
      carResult?.year,
      carResult?.make,
      carResult?.model,
      carResult?.carVariationLovId,
      selectedSolution?.solutionId ?? carResult?.solutionId,
      carResult?.bedTypeLovId
    );

    let dropDown = null;
    let matchingSolution = null;
    //populate roof rack system dropdown
    if (carResult?.solutions?.length > 0) {
      const solutions = carResult.solutions.filter(
        (x) => isValidSolutionType(x.solutionType, true) && x.statusErp === 'Sellable'
      );
      const processedSolutions = solutions.map((x) => {
        // Normalize webtitle1 so variants with different spacing/casing share a group key.
        let groupKey = x.dataProductWebtitle1.replaceAll(' ', '').replaceAll('_', '').toLowerCase();
        const referenceData = getSolutionReferenceComponents(x);
        const oversizedFoot = referenceData?.find((r) => r.Title === 'Foot' && r.ProductId === '710801');
        // Oversized foot variants are treated as a separate product family in the dropdown.
        if (oversizedFoot) groupKey += 'oversizedfoot';
        return { solution: x, referenceData, groupKey, oversizedFoot };
      });

      const solutionsByGroupKey = processedSolutions.reduce((acc, item) => {
        if (!acc[item.groupKey]) acc[item.groupKey] = [];
        acc[item.groupKey].push(item);
        return acc;
      }, {});

      dropDown = [];
      Object.values(solutionsByGroupKey).forEach((items) => {
        const { groupKey, oversizedFoot } = items[0];
        const distinctColors = new Set(items.map(({ solution }) => solution.dataProductColorSpecification));
        // Color variants are grouped so the user can switch color via the swatch selector.
        // Same-color variants (e.g. different sizes) stay separate and are disambiguated with webtitle2.
        const shouldGroup = items.length > 1 && distinctColors.size > 1;

        if (shouldGroup) {
          let label = items[0].solution.dataProductWebtitle1.replaceAll('_', ' ');
          if (oversizedFoot) label += ' - ' + oversizedFoot.Webtitle1;
          dropDown.push({
            key: groupKey,
            groupKey,
            label,
            solutions: items.map(({ solution, referenceData }) => ({ ...solution, referenceData })),
          });
        } else {
          items.forEach(({ solution: x, referenceData, oversizedFoot }) => {
            let label = x.dataProductWebtitle1.replaceAll('_', ' ');
            if (oversizedFoot) label += ' - ' + oversizedFoot.Webtitle1;
            if (items.length > 1 && x.dataProductWebtitle2) {
              label += ' - ' + x.dataProductWebtitle2.replaceAll('_', ' ');
            }
            dropDown.push({
              key: x.solutionId,
              groupKey,
              label,
              solutions: [{ ...x, referenceData }],
            });
          });
        }
      });

      if (queryParameters?.solutionId && dropDown?.length > 0) {
        matchingSolution = dropDown.reduce((acc, x) => {
          const solution = x.solutions.find((solution) => solution.solutionId === queryParameters?.solutionId);
          return solution ? { dd: x, solution } : acc;
        }, null);
      }
    }

    if (dropDown) {
      const sortOrder = [
        'thulewingbaredge',
        'thulewingbarevo',
        'thulewingbarevooversizedfoot',
        'thulesquarebarevo',
        'thulesquarebarevooversizedfoot',
        'thuleprobarevo',
        'thulecaprock',
      ];
      dropDown.sort((a, b) => {
        // Sort by product family first (groupKey), then alphabetically within the family.
        const aIndex = sortOrder.indexOf(a.groupKey);
        const bIndex = sortOrder.indexOf(b.groupKey);

        if (aIndex !== bIndex) {
          if (aIndex === -1 && bIndex === -1) {
            return a.groupKey.localeCompare(b.groupKey);
          }
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        }
        return a.label.localeCompare(b.label);
      });
    }

    setSolutionsDropDown(dropDown);
    if (matchingSolution) {
      setSelectedSolution(matchingSolution?.solution);
      setSelectedSolutionsDropDown(matchingSolution?.dd);
    } else if (dropDown?.length > 0) {
      setSelectedSolutionsDropDown(dropDown[0]);
      setSelectedSolution(dropDown[0].solutions[0]);
    } else {
      setSelectedSolution(null);
      setSelectedSolutionsDropDown(null);
    }
  }, [carResult]);

  const addProductsToList = () => {
    let updatedList = [...productList];
    selectedSolution.referenceData.forEach((referenceDataItem) => {
      const existing = updatedList.find((x) => x.ProductId === referenceDataItem.ProductId);
      if (existing) existing.Quantity += 1;
      else updatedList.push({ ...referenceDataItem, Quantity: 1 });
    });
    track_addToList(
      selectedSolution.referenceData?.map((x) => x.ProductId),
      TRACK_EVENT_TYPE.CARFIT
    );
    setProductList(updatedList);
    setNotificationMessage(translations.productAddedToList);
  };

  const clearSettings = () => {
    setSelectedSolution(null);
    setSelectedSolutionsDropDown(null);
    setSolutionDataProduct(null);
    setSolutionsDropDown(null);
    carSelectorRef.current.resetSelection();
    carSelectorDesktopRef.current.resetSelection();
    navigate(
      {
        pathname: location.pathname,
        search: '',
      },
      { replace: true }
    );
  };

  const copyLink = () => {
    const url = window.location.href;
    track_shareLink(TRACK_EVENT_TYPE.CARFIT);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setNotificationMessage(translations.copiedLink);
      })
      .catch((err) => {});
  };

  const loadSolutionDataProduct = () => {
    let products = [{ ProductId: selectedSolution.dataProductId, Quantity: 1 }];

    const kitComponent = selectedSolution?.referenceData?.find(
      (x) => x.ReferenceTypeId?.toLowerCase() === SOLUTION_REFERENCE_TYPE.RACKSOLUTION_KIT.toLowerCase()
    );
    if (kitComponent) products.push({ ProductId: kitComponent.ProductId, Quantity: 1 });
    const payload = {
      productsInput: products,
      additionalData: true,
    };

    fetch('/api/sitecore/fitguide/GetRetailerFitGuideProductListData', {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.ListProducts?.length > 0) {
          const listProduct = data.ListProducts.find((x) => x.ProductId === selectedSolution.dataProductId);
          const kitProduct = data.ListProducts.find((x) => x.ProductId === kitComponent?.ProductId);
          const dropDownProductIds = selectedSolutionsDropDown?.solutions.map((x) => x.dataProductId);
          listProduct.AvailableSizeVariants = listProduct.SizeVariants?.filter((x) =>
            dropDownProductIds?.includes(x.ProductId)
          );
          setSolutionDataProduct({
            KitProduct: kitProduct,
            Product: listProduct,
            TotalPriceFormatted: data?.TotalPriceFormatted,
          });
          setActiveColor(listProduct.ColorSwatch?.find((x) => x.IsActive));
          setActiveSize(listProduct.AvailableSizeVariants.find((x) => x.IsActive));
        }
      })
      .catch((error) => console.error('Error:', error));
  };

  const getLinkWithQueryForFitAssistant = () => {
    const variationId = carResult?.bedTypeLovId ? carResult?.bedTypeLovId : carResult?.carVariationLovId;
    return `${solutionDataProduct.Product.ProductUrl}?fitassistant_year_first=${encodeURIComponent(`year:${carResult?.year},make:${carResult?.make},model:${carResult?.model},variation:${variationId}`)}`;
  };

  const measurementImage = _THULEDATA.retailerFitGuide?.measurementImage;
  const showClampImage = selectedSolution?.solutionMaxBarSpreadIn > 0 && measurementImage;
  const productCategories = Object.hasOwn(carResult ?? {}, 'ProductMountData')
    ? carResult.ProductMountData
    : selectedSolution?.FittingProductCategories;

  const ColorSelector = memo(
    ({ solutionDataProduct, activeColor, setActiveColor, solutionsDropDown, setSelectedSolution }) => {
      if (!solutionDataProduct?.Product.ColorSwatch?.length) return null;

      return (
        <div className="color-selector-container color-selector-container--large">
          <div className="color-selector mt-4" id="color-swatch">
            <ul className="list-inline mb-0">
              {solutionDataProduct.Product.ColorSwatch.map((color, index) => (
                <li
                  className={`pe-lg-2 mt-3 `}
                  key={index}
                  onMouseEnter={() => setActiveColor(color)}
                  onMouseLeave={() => setActiveColor(solutionDataProduct.Product.ColorSwatch.find((x) => x.IsActive))}
                >
                  <button
                    onClick={() => {
                      const matchingSolution = solutionsDropDown.reduce((acc, x) => {
                        const match = x.solutions.find((solution) => solution.dataProductId === color.ProductId);
                        return match ? match : acc;
                      }, null);
                      if (matchingSolution) {
                        setSelectedSolution(matchingSolution);
                      }
                    }}
                    style={{ backgroundImage: `url(${color.AssetURL})` }}
                    title={color.AssetDescription}
                    className={`tag-manager-color-variation ${color === activeColor ? 'active' : ''}`}
                    data-component-label="Select color"
                    href={`${window.location.origin}/${_THULEDATA.SitecoreLanguage?.toLowerCase()}-_-${color.ProductId}`}
                    data-pimid={color.ProductId}
                  >
                    <span className="sr-only">
                      {color.AssetDescription} {color === activeColor ? '(selected)' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {activeColor && <p className="paragraph-s selected-swatch-name mt-4 mb-0">{activeColor.Name}</p>}
        </div>
      );
    }
  );

  const onClickVariant = (variant) => {
    const solutionsDropDownItem = selectedSolutionsDropDown.solutions.find(
      (x) => x.dataProductId === variant.ProductId
    );
    if (solutionsDropDownItem) {
      setSelectedSolution(solutionsDropDownItem);
      setActiveSize(variant);
    }
  };

  const SizeSelector = memo(({ solutionDataProduct, activeSize }) => {
    if (!solutionDataProduct?.Product?.AvailableSizeVariants?.length) return null;
    return (
      <>
        <h2 className="heading5 mb-2">Size</h2>
        <div className="PDP_sizeSelector">
          {solutionDataProduct.Product.AvailableSizeVariants.map((sizeVariant, i) => (
            <button
              onClick={() => onClickVariant(sizeVariant)}
              className={`btn ${activeSize == sizeVariant ? 'active' : ''}`}
              key={i}
            >
              {sizeVariant.Text}
            </button>
          ))}
        </div>
      </>
    );
  });

  const isFactoryInstalledCrossbar = Object.hasOwn(carResult ?? {}, 'ProductMountData');
  return (
    <div
      className={`retailerFitGuide_PDP p-0 ${isFactoryInstalledCrossbar ? 'fixbar' : ''} ${(carResult && selectedSolution) || isFactoryInstalledCrossbar ? 'hasResult' : ''}`}
    >
      {/* MOBILE */}
      <div className="d-lg-none">
        <RetailerFitGuide_CarSelector
          journey={TRACK_EVENT_TYPE.CARFIT}
          visible={true}
          ref={carSelectorRef}
          result={carResult}
          setResult={setCarResult}
          queryParameters={queryParameters}
          includeGenerationOld={false}
          title={translations.selectCar}
          summaryText={translations.selectedCar}
          allowDiscontinued={false}
        />
        {solutionsDropDown?.length > 0 && (
          <RetailerFitGuide_Select
            // loading={loadingVariations}
            dropDownValue={selectedSolutionsDropDown}
            setDropDownValue={onChangeSelectedSolution}
            name={translations.roofRackSystem}
            options={solutionsDropDown}
            optionPropertyName="label"
            optionPropertyValue={'key'}
            skipSuccessIcon={true}
          />
        )}
        {(selectedSolution || carResult?.solutionId) && (
          <div className="mt-3 mb-7">
            <RetailerFitGuide_ClearCar clearEvent={clearSettings} />
            {isFactoryInstalledCrossbar && (
              <button onClick={copyLink} className="btn d-flex mt-5 p-0 justify-content-center align-items-center mt-3">
                <i className="le-icon-share"></i>
                <span className="heading6 ms-2">{translations.shareableLink}</span>
              </button>
            )}
          </div>
        )}
        {carResult && !solutionsDropDown?.length > 0 && !isFactoryInstalledCrossbar && (
          <p className="paragraph mt-7">{translations.noAvailableSystems}.</p>
        )}
      </div>
      {selectedSolution?.productImages?.length > 0 && (
        <ImageSlider
          className="d-lg-none mb-7"
          images={selectedSolution.productImages.map((x) => {
            return { src: x.ImageURL, alt: x.ImageAltText };
          })}
          enableThumbnailCarousel={true}
        />
      )}

      <div className="PDP_buyArea--mobile d-lg-none">
        <ColorSelector
          solutionDataProduct={solutionDataProduct}
          activeColor={activeColor}
          setActiveColor={setActiveColor}
          solutionsDropDown={solutionsDropDown}
          setSelectedSolution={setSelectedSolution}
        />

        <SizeSelector solutionDataProduct={solutionDataProduct} activeSize={activeSize} />

        {selectedSolution && (
          <>
            <h1 className="heading3 mb-2">{selectedSolution.dataProductWebtitle1}</h1>
            <h2 className="paragraph--s text-color--gray80 mb-5">{solutionDataProduct?.Product?.WebTitle2}</h2>
            <div className="PDP_priceAndShare">
              <p className="paragraph mb-0">MSRP {selectedSolution?.TotalPriceFormatted}</p>
              <button onClick={copyLink} className="btn">
                <i className="le-icon-share"></i>
                <span className="heading6 ms-2">{translations.shareableLink}</span>
              </button>
            </div>
          </>
        )}
      </div>

      {carResult && (isFactoryInstalledCrossbar || selectedSolution) && (
        <div className="rfg_pdp_common">
          {selectedSolution?.productImages?.length > 0 && (
            <RetailerFitGuide_ImageGrid
              images={selectedSolution.productImages.map((x) => {
                return { src: x.ImageURL, alt: x.ImageAltText };
              })}
            />
          )}
          {selectedSolution && (
            <>
              <div className="mb-9">
                <h2 className="heading4 mb-6">{translations.rackComponents}</h2>
                {selectedSolution?.referenceData &&
                  selectedSolution.referenceData.map((referenceDataItem, index) => {
                    if (referenceDataItem.Webtitle1)
                      return (
                        <dl key={index} className="PDP_rackComponent">
                          <dt className="text-color--gray80 paragraph--s">
                            {getReferenceDisplayNameById(referenceDataItem.ReferenceTypeId)}:
                          </dt>
                          <dd className="d-flex justify-content-between align-items-start heading5">
                            <span>{referenceDataItem.Webtitle1}</span>
                            <span className="retailerFitGuide_highlight">{referenceDataItem.ProductId}</span>
                          </dd>
                        </dl>
                      );
                  })}
              </div>
              <RetailerFitGuide_TechSpecs showClampImage={showClampImage} solution={selectedSolution} />

              {showClampImage && (
                <div className="mt-7">
                  <img src={measurementImage.ImageURL} alt={measurementImage.ImageAltText} />
                </div>
              )}

              {solutionDataProduct?.KitProduct?.FittingInstruction?.AssetURL && (
                <>
                  <h2 className="heading4 mb-6 mt-7">{translations.instructions}</h2>
                  <RetailerFitGuide_Downloads
                    files={[
                      {
                        fileName: `User manual - ${solutionDataProduct?.KitProduct.WebTitle1}`,
                        url: solutionDataProduct?.KitProduct?.FittingInstruction.AssetURL,
                      },
                    ]}
                  />
                </>
              )}
              {solutionDataProduct?.Product && (
                <a
                  onClick={() => track_linkClick(TRACK_EVENT_TYPE.INSTRUCTIONS)}
                  className="PDP_btn_instructions my-9 btn thule-btn-secondary btn-sm"
                  target="_blank"
                  href={getLinkWithQueryForFitAssistant()}
                  rel="noreferrer"
                >
                  <i className="le-icon-square-arrow-out-up-right"></i>
                  <span className="heading6">{translations.viewOnThule}</span>
                </a>
              )}
              <RetailerFitGuide_FitTips
                initExpand={true}
                journey={TRACK_EVENT_TYPE.CARFIT}
                solution={selectedSolution}
              />
            </>
          )}
          <RetailerFitGuide_CompatibleProducts fittingProductCategories={productCategories} />
          {selectedSolution && (
            <button onClick={addProductsToList} className="btn_addToList btn thule-btn-default d-lg-none">
              {translations.addToList}
            </button>
          )}
        </div>
      )}

      <div className="PDP_buyArea--desktop w-100 d-none d-lg-block">
        <RetailerFitGuide_CarSelector
          journey={TRACK_EVENT_TYPE.CARFIT}
          visible={true}
          ref={carSelectorDesktopRef}
          result={carResult}
          setResult={setCarResult}
          queryParameters={queryParameters}
          includeGenerationOld={false}
          title={translations.selectCar}
          summaryText={translations.selectedCar}
          allowDiscontinued={false}
        />
        {solutionsDropDown?.length > 0 && (
          <div>
            <RetailerFitGuide_Select
              // loading={loadingVariations}
              dropDownValue={selectedSolutionsDropDown}
              setDropDownValue={onChangeSelectedSolution}
              name={translations.roofRackSystem}
              options={solutionsDropDown}
              optionPropertyName="label"
              optionPropertyValue={'key'}
              skipSuccessIcon={true}
            />
          </div>
        )}
        {carResult && !solutionsDropDown?.length > 0 && !Object.hasOwn(carResult, 'ProductMountData') && (
          <p className="paragraph mt-7">{translations.noAvailableSystems}.</p>
        )}
        {(selectedSolution || carResult?.solutionId) && (
          <div className="mt-3 mb-7">
            <RetailerFitGuide_ClearCar clearEvent={clearSettings} />
            {isFactoryInstalledCrossbar && (
              <button onClick={copyLink} className="btn d-flex mt-5 p-0 justify-content-center align-items-center mt-3">
                <i className="le-icon-share"></i>
                <span className="heading6 ms-2">{translations.shareableLink}</span>
              </button>
            )}
          </div>
        )}
        {selectedSolution && (
          <>
            <h1 className="heading3 mb-2">{selectedSolution.dataProductWebtitle1}</h1>
            <h2 className="paragraph--s text-color--gray80 mb-5">{solutionDataProduct?.Product?.WebTitle2}</h2>
            <div className="PDP_priceAndShare">
              <p className="paragraph mb-0">MSRP {selectedSolution?.TotalPriceFormatted}</p>
              <button onClick={copyLink} className="btn">
                <i className="le-icon-share"></i>
                <span className="heading6 ms-2">{translations.shareableLink}</span>
              </button>
            </div>
          </>
        )}

        <ColorSelector
          solutionDataProduct={solutionDataProduct}
          activeColor={activeColor}
          setActiveColor={setActiveColor}
          solutionsDropDown={solutionsDropDown}
          setSelectedSolution={setSelectedSolution}
        />
        <SizeSelector solutionDataProduct={solutionDataProduct} activeSize={activeSize} />
        {selectedSolution && (
          <button onClick={addProductsToList} className="btn_addToList btn thule-btn-default">
            {translations.addToList}
          </button>
        )}
      </div>
    </div>
  );
};

export default RetailerFitGuide_PDP;
